import { Component, OnInit } from '@angular/core';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { MatterState, TITLE_SEARCH_MATTER_STATE } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { NavigationEnd, Router } from '@angular/router';
import { CheckSyncComponent } from '../check-sync/check-sync.component';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { MatDialog } from '@angular/material/dialog';
import { filter } from 'rxjs/operators';
import { MatterDispatch } from '@state/matter';
import { TestHooks } from './stepper.hooks';

interface Page {
	page: TITLE_SEARCH_MATTER_STATE;
	before?: TITLE_SEARCH_MATTER_STATE;
	after: TITLE_SEARCH_MATTER_STATE;
	index: number;
	selected: boolean;
	text: string;
}

const flow: Page[] = [
	{
		page: TITLE_SEARCH_MATTER_STATE.SEARCH,
		after: TITLE_SEARCH_MATTER_STATE.SYNC,
		index: 0,
		selected: true,
		text: 'SEARCH',
	},
	{
		page: TITLE_SEARCH_MATTER_STATE.SYNC,
		before: TITLE_SEARCH_MATTER_STATE.SEARCH,
		after: TITLE_SEARCH_MATTER_STATE.INSTRUMENTS,
		index: 1,
		selected: false,
		text: 'SYNC',
	},
	{
		page: TITLE_SEARCH_MATTER_STATE.INSTRUMENTS,
		before: TITLE_SEARCH_MATTER_STATE.SYNC,
		after: TITLE_SEARCH_MATTER_STATE.ADJACENT_ANALYSIS,
		index: 2,
		selected: false,
		text: 'INSTRUMENTS',
	},
	{
		page: TITLE_SEARCH_MATTER_STATE.ADJACENT_ANALYSIS,
		before: TITLE_SEARCH_MATTER_STATE.INSTRUMENTS,
		after: TITLE_SEARCH_MATTER_STATE.WRITS,
		index: 3,
		selected: false,
		text: 'ADJACENT ANALYSIS',
	},
	{
		page: TITLE_SEARCH_MATTER_STATE.WRITS,
		before: TITLE_SEARCH_MATTER_STATE.ADJACENT_ANALYSIS,
		after: TITLE_SEARCH_MATTER_STATE.ACTIONS,
		index: 4,
		selected: false,
		text: 'WRITS',
	},
	{
		page: TITLE_SEARCH_MATTER_STATE.ACTIONS,
		before: TITLE_SEARCH_MATTER_STATE.WRITS,
		after: TITLE_SEARCH_MATTER_STATE.COMPLETE,
		index: 5,
		selected: false,
		text: 'ACTIONS',
	},
];

@UntilDestroy()
@Component({
	selector: 'app-stepper',
	templateUrl: './stepper.component.html',
	styleUrls: ['./stepper.component.scss'],
	standalone: false,
})
export class StepperComponent implements OnInit {
	testHooks = TestHooks;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(SelectedMatter.matterState) matterState$: Observable<MatterState>;

	public titleSearchStates = TITLE_SEARCH_MATTER_STATE;
	public currentState: Page;
	public pages = flow;
	public showActionsComponent = true;

	constructor(
		private routeService: NavigationService,
		private router: Router,
		private dialog: MatDialog,
		private matterDispatch: MatterDispatch,
	) {
		this.matterState$.pipe(untilDestroyed(this)).subscribe((state) => {
			if (state && this.currentState?.page !== state.titleSearch) {
				const _page = this.pages.find(
					(page: Page) => page.page === state.titleSearch,
				);

				this.currentState = _page;

				this.pages.forEach((page: Page) => {
					page.selected = state.titleSearch === page.page;
				});

				switch (state.titleSearch) {
					case TITLE_SEARCH_MATTER_STATE.SEARCH:
						this.routeService.goToMatter_TitleSearch_Stepper_Search(
							this.matterId,
						);
						break;
					case TITLE_SEARCH_MATTER_STATE.SYNC:
						this.routeService.goToMatter_TitleSearch_Stepper_Sync(
							this.matterId,
						);
						break;
					case TITLE_SEARCH_MATTER_STATE.ADJACENT_ANALYSIS:
						this.routeService.goToMatter_TitleSearch_Stepper_AdjacentAnalysis(
							this.matterId,
						);
						break;
					case TITLE_SEARCH_MATTER_STATE.INSTRUMENTS:
						this.routeService.goToMatter_TitleSearch_Stepper_Instruments(
							this.matterId,
						);
						break;
					case TITLE_SEARCH_MATTER_STATE.ACTIONS:
						this.routeService.goToMatter_TitleSearch_Stepper_Requisitions(
							this.matterId,
						);
						break;
					case TITLE_SEARCH_MATTER_STATE.WRITS:
						this.routeService.goToMatter_TitleSearch_Stepper_Writs(
							this.matterId,
						);
						break;
					case TITLE_SEARCH_MATTER_STATE.COMPLETE:
						this.routeService.goToMatter_TitleSearch(this.matterId);
						break;
				}
			}
		});
	}

	setState(page: Page) {
		this.matterDispatch.SetTitleState(this.matterId, page.page);
	}

	skip(): void {
		this.matterDispatch.SetTitleState(
			this.matterId,
			TITLE_SEARCH_MATTER_STATE.COMPLETE,
		);
	}

	ngOnInit(): void {
		this.router.events
			.pipe(
				untilDestroyed(this),
				filter((event) => event instanceof NavigationEnd),
				filter(
					(event: NavigationEnd) =>
						event.url ===
						UrlService.Matter_TitleSearch_Stepper_Requisitions(this.matterId),
				),
				filter(() => this.showActionsComponent),
			)
			.subscribe(() => {
				this.dialog.open(CheckSyncComponent);
				this.showActionsComponent = false;
			});
	}
}
